/* eslint-disable eqeqeq */
/* eslint-disable array-callback-return */
import { Motion, spring } from "react-motion";
import { Box, Button, Checkbox, HStack, Skeleton, Stack, Text } from "@chakra-ui/react";
import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import AppColors from "../../../constants/AppColors";
import PerfectScrollbar from "react-perfect-scrollbar"
import { defaultStyle, OPEN_STATUS, RESOLVED_STATUS } from "../constants";
import { MentionsInput, Mention } from 'react-mentions'
import { formatDistanceStrict } from "date-fns";
import { AuditSheetContext } from "../../../providers/AuditSheetProvider";
import { useContext } from "react";
import RenderMessage from "./RenderMessage";
import { RebuttalIcon } from "../../../constants/IconData";
import { successToast } from "../../../components/successToast";
import { useToastr } from "@laxmimanogna/code-quick-components";


const CommentBox = ({ showMenu, metaData, closeComment, coords }) => {
    const toast = useToastr();
    const auditSheetContext = useContext(AuditSheetContext);
    const { currentUpload, users, isPostCommentLoading, comments, setComments, isCommentLoading, postComment, commentFlags, setCommentFlags, allUsers, allComments, setAllComments } =
        auditSheetContext;

    const { rowIndex, selectedRowData } = metaData;
    const isRebuttalMode = currentUpload?.status === 'REBUTTAL IN PROGRESS';
    const isClientRebuttal = currentUpload?.status === 'CLIENT REBUTTAL';

    // All row-level comments combined
    const rowComments = comments.filter(c => !c.audit_sheet_columns?.length && !['ADD', 'DELETE'].includes(c.action));
    const latestThread = rowComments.length > 0 ? rowComments[rowComments.length - 1] : null;

    const [message, setMessage] = useState('');
    const [isInternal, setIsInternal] = useState(false);

    const boxRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [boxPosition, setBoxPosition] = useState({ x: -9999, y: -9999 });

    useEffect(() => {
        if (scrollbarRef.current) {
            scrollbarRef.current.scrollTop = scrollbarRef.current.scrollHeight;
        }
    }, [rowComments]);

    useLayoutEffect(() => {
        if (!showMenu) {
            setBoxPosition({ x: -9999, y: -9999 });
            return;
        }
        if (boxRef.current) {
            const rect = boxRef.current.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const MARGIN = 10;
            let x = coords.x + 20;
            let y = coords.y;
            if (x + rect.width > vw - MARGIN) x = vw - rect.width - MARGIN;
            if (x < MARGIN) x = MARGIN;
            if (y + rect.height > vh - MARGIN) y = coords.y - rect.height - 5;
            if (y < MARGIN) y = MARGIN;
            setBoxPosition({ x, y });
        }
    }, [showMenu, coords]);

    const getStatus = () => {
        if (!latestThread) return 'Open';
        const replies = latestThread.reply_comments || [];
        let status = 'Open';
        for (const reply of replies) {
            const action = reply.action;
            if (action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
            else if (action === 'REBUTTAL-REJECTED') status = 'Rejected';
            else if (action === 'REBUTTAL-RESOLVED') status = 'Closed';
            else if (RESOLVED_STATUS.includes(action)) status = 'Resolved';
            else if (action === 'RE-OPENED') status = latestThread?.action === 'REBUTTAL' ? 'Accepted' : 'Open';
        }
        return status;
    };

    const threadStatus = getStatus();
    const isAccepted = threadStatus === 'Accepted';
    const isRejected = threadStatus === 'Rejected';
    const isClosed = threadStatus === 'Closed' || threadStatus === 'Resolved';

    // In rebuttal mode, block input only when rejected or closed (same as QA portal)
    const inputBlocked = isRebuttalMode && (isRejected || isClosed);

    const getHeaderBg = () => {
        if (isRebuttalMode || isClientRebuttal) return '#1e293b';
        if (isRejected || isClosed) return AppColors.darkGray;
        return AppColors.secondary;
    };

    const getStatusBadgeBg = () => {
        if (isAccepted) return '#16a34a';
        if (isRejected) return AppColors.danger;
        if (isClosed) return 'rgba(255,255,255,0.15)';
        return 'rgba(255,255,255,0.25)';
    };

    const onSubmitReply = async () => {
        const rowId = selectedRowData?.id;
        const chartId = currentUpload?.id;
        if (!rowId || !chartId || !message.trim()) return;

        const body = {
            audit_sheet_rows: [rowId],
            audit_sheet_columns: [],
            action: OPEN_STATUS,
            comment: message,
            chart: chartId,
            tagged_user: [],
            is_internal: isInternal,
            ...(latestThread ? { parent: latestThread.id } : {}),
        };

        const strArray = message.match(/@\[(.\d*?)]/g);
        if (strArray?.length) {
            const tagged = [];
            strArray.forEach(str => {
                const match = str.match(/@\[(.\d*?)]/);
                if (match && !tagged.includes(match[1])) tagged.push(Number(match[1]));
            });
            body.tagged_user = tagged;
        }

        const response = await postComment(body);
        if (response?.id) {
            const updater = prev => {
                if (response.parent) {
                    return prev.map(c =>
                        c.id === response.parent
                            ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                            : c
                    );
                }
                return [...prev, { ...response, reply_comments: [] }];
            };
            setComments(updater);
            setAllComments(updater);

            const updatedFlags = { ...commentFlags };
            [rowId].forEach(id => {
                updatedFlags[id] = { ...(commentFlags[id] || {}), '__row__': true };
            });
            setCommentFlags(updatedFlags);
            successToast(toast, { header: 'Success!', description: 'Reply sent.' });
            setMessage('');
            setIsInternal(false);
        }
    };

    const renderCommentBody = (comment, key, isFirst = false) => {
        const fullName = `${comment.user.first_name} ${comment.user.last_name}`;
        const isStatus = !comment.comment;
        const STATUS_LABEL = {
            'RE-OPENED': 'Re-opened',
            'RESOLVED': 'Marked as resolved',
            'REBUTTAL-RESOLVED': 'Closed by auditor',
            'REBUTTAL-ACCEPTED': 'Accepted rebuttal',
            'REBUTTAL-REJECTED': 'Rejected rebuttal',
        };
        return (
            <Box
                key={key}
                px={3} py={2}
                borderTop={isFirst ? 'none' : '1px solid'}
                borderColor='gray.100'
            >
                <HStack spacing={1} align='baseline' mb={isStatus ? 0 : 1}>
                    <Text fontSize='sm' fontWeight='semibold' color={AppColors.secondary} lineHeight='1.3'>{fullName}</Text>
                    <Text fontSize='xs' color='gray.400'>
                        {comment.commented_date
                            ? formatDistanceStrict(new Date(comment.commented_date), new Date(), { addSuffix: true })
                            : ''}
                    </Text>
                    {comment.action === 'REBUTTAL' && <RebuttalIcon style={{ width: 13, height: 13 }} />}
                    {comment.is_internal && (
                        <Box px={1.5} py='1px' bg='purple.100' borderRadius='full' border='1px solid' borderColor='purple.200'>
                            <Text fontSize='9px' fontWeight='bold' color='purple.600' letterSpacing='wide'>INTERNAL</Text>
                        </Box>
                    )}
                </HStack>
                {isStatus
                    ? <Text as='i' fontSize='xs' color={AppColors.darkGray}>{STATUS_LABEL[comment.action] || comment.action}</Text>
                    : <RenderMessage comment={comment} allUsers={allUsers} />
                }
            </Box>
        );
    };

    const renderLoader = () => (
        <Stack px={2} py={1}>
            <Skeleton height='12px' width='80px' />
            <Skeleton height='12px' />
            <Skeleton height='12px' width='60px' mt={3} />
            <Skeleton height='12px' />
        </Stack>
    );

    return (
        <Motion defaultStyle={{ opacity: 0 }} style={{ opacity: !showMenu ? spring(0) : spring(1) }}>
            {(interpolatedStyle) => (
                <>
                    {showMenu && (
                        <Box
                            ref={boxRef}
                            zIndex={1000}
                            bg='white'
                            overflow='hidden'
                            border='1px solid'
                            borderColor='gray.200'
                            style={{
                                borderRadius: '12px',
                                position: 'fixed',
                                top: boxPosition.y,
                                left: boxPosition.x,
                                opacity: interpolatedStyle.opacity,
                                width: 'clamp(300px, 28vw, 400px)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                            }}
                        >
                            {/* Header */}
                            <HStack px={3} py={1.5} bg={getHeaderBg()} justifyContent='space-between'>
                                <HStack spacing={2}>
                                    <Box px={2} py='1px' bg='rgba(0,0,0,0.18)' borderRadius='full' flexShrink={0}>
                                        <Text fontSize='10px' fontWeight='bold' color='white' letterSpacing='wide' whiteSpace='nowrap'>
                                            {(isRebuttalMode || isClientRebuttal) ? 'REBUTTAL REVIEW' : 'COMMENTS'}
                                        </Text>
                                    </Box>
                                    {rowIndex != null && (
                                        <Text fontSize='xs' color='rgba(255,255,255,0.8)'>Row {rowIndex + 1}</Text>
                                    )}
                                </HStack>
                                <HStack spacing={1.5}>
                                    {rowComments.length > 0 && (isRebuttalMode || isClientRebuttal) && (
                                        <Box px={2} py='1px' borderRadius='full' bg={getStatusBadgeBg()}>
                                            <Text fontSize='10px' fontWeight='bold' color='white' whiteSpace='nowrap'>
                                                {threadStatus}
                                            </Text>
                                        </Box>
                                    )}
                                    <Box
                                        cursor='pointer'
                                        onClick={closeComment}
                                        color='rgba(255,255,255,0.8)'
                                        _hover={{ color: 'white' }}
                                        fontSize='sm'
                                        lineHeight={1}
                                        fontWeight='bold'
                                        px={1}
                                    >
                                        ✕
                                    </Box>
                                </HStack>
                            </HStack>

                            {/* Thread — flat combined display */}
                            {isCommentLoading
                                ? <Box p={3}>{renderLoader()}</Box>
                                : rowComments.length > 0 && (
                                    <PerfectScrollbar
                                        containerRef={el => { scrollbarRef.current = el; }}
                                        style={{ maxHeight: '180px' }}
                                    >
                                        {rowComments.map((c, i) => (
                                            <React.Fragment key={c.id ?? i}>
                                                {renderCommentBody(c, `${i}-root`, i === 0)}
                                                {c.reply_comments?.map((r, j) => renderCommentBody(r, `${i}-${j}`, false))}
                                            </React.Fragment>
                                        ))}
                                    </PerfectScrollbar>
                                )
                            }

                            {/* Input / action section */}
                            <Box
                                px={3} pb={3} pt={2}
                                borderTop={rowComments.length > 0 && !isCommentLoading ? '1px solid' : 'none'}
                                borderColor='gray.100'
                            >
                                {/* Rebuttal mode: state hints */}
                                {isRebuttalMode && rowComments.length === 0 && !isCommentLoading && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor='gray.300' spacing={1}>
                                        <Text fontSize='xs' color={AppColors.darkGray}>No rebuttal comments for this row</Text>
                                    </HStack>
                                )}
                                {isRebuttalMode && isRejected && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.danger} spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.danger}>Rebuttal rejected — original finding stands</Text>
                                    </HStack>
                                )}
                                {isRebuttalMode && isClosed && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.darkGray} spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.darkGray}>Rebuttal resolved</Text>
                                    </HStack>
                                )}
                                {isRebuttalMode && isAccepted && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor='#16a34a' spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color='#15803d'>Accepted — close the rebuttal via the notes drawer</Text>
                                    </HStack>
                                )}
                                {(isRebuttalMode || isClientRebuttal) && threadStatus === 'Open' && rowComments.length > 0 && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor='#f59e0b' spacing={1}>
                                        <Box w={2} h={2} borderRadius='full' bg='#f59e0b' flexShrink={0} />
                                        <Text fontSize='xs' color='#92400e'>Awaiting QA decision</Text>
                                    </HStack>
                                )}

                                {/* Reply input — shown when a thread exists and input is not blocked */}
                                {rowComments.length > 0 && !inputBlocked && (
                                    <>
                                        <MentionsInput
                                            placeholder='Add a reply...'
                                            value={message}
                                            onChange={e => setMessage(e.target.value)}
                                            style={{ ...defaultStyle, control: { ...defaultStyle.control, width: '100%' } }}
                                            allowSpaceInQuery
                                        >
                                            <Mention
                                                displayTransform={id => {
                                                    const u = users.find(u => u.id == id);
                                                    return u ? `@${u.first_name} ${u.last_name}` : `@${id}`;
                                                }}
                                                trigger='@'
                                                data={users}
                                                markup='@[__id__]'
                                                style={{ fontWeight: 500, backgroundColor: AppColors.bgColor }}
                                            />
                                        </MentionsInput>
                                        <Checkbox
                                            size='sm'
                                            colorScheme='purple'
                                            mt={1}
                                            isChecked={isInternal}
                                            onChange={e => setIsInternal(e.target.checked)}
                                        >
                                            <Text fontSize='xs' color='gray.500'>Mark Internal</Text>
                                        </Checkbox>
                                        <Button
                                            mt={2} w='100%' size='sm'
                                            bgColor={AppColors.secondary}
                                            borderRadius='md' color='white'
                                            isDisabled={!message}
                                            isLoading={isPostCommentLoading}
                                            onClick={onSubmitReply}
                                            _hover={{ bgColor: AppColors.secondary, opacity: 0.9 }}
                                        >
                                            Send
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Motion>
    );
};
export default CommentBox;
